import React, { useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Line } from 'react-chartjs-2';
import {
  CategoryScale, Chart as ChartJS, Filler, LinearScale, LineElement, PointElement, Tooltip,
} from 'chart.js';
import {
  Activity, AlertTriangle, BellOff, Bug, ChevronDown, ChevronRight, CircleGauge, CirclePause, Cpu, ExternalLink,
  HardDrive, HeartPulse, Layers, LineChart, Lightbulb, MemoryStick, PackageOpen, RotateCcw, Search,
  ShieldAlert, SlidersHorizontal, Thermometer, TrendingUp, Wifi, WifiOff, CloudDownload,
} from 'lucide-react';
import { analyticsDb } from '../../firebase';
import { macToSlug } from '../../utils/macSlug';
import { PROBLEM_LABEL, RULE_META, problemValue } from '../../utils/analyticsRules';
import { PanelCard, StatCard } from '../DashboardCards';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

// "Fleet health" page (/health, MDM analytics). Everything comes from ONE Firestore document, mdm-analitics
// fleet/summary, written by the fleet scan - one read per scan no matter how many devices there are.
//
// Reading order: KPI tiles -> Problems (pick a problem type and/or a group; the list then shows exactly that
// problem on each device) -> groups (click = show that group's problems above) + availability.
// The chosen problem / group / search live in the URL (?problem=&group=&q=), so a view can be shared.
// Phone layout: 2 KPI tiles and 2 problem cards per row, device rows stack, the filter bar stays visible
// while scrolling the list, and nothing scrolls inside the page scroll.

const ICON = 16;
const NO_GROUP = '_none'; // URL value for "devices without a group"
const GROUPS_ON_PHONE = 8;
const DEVICES_ON_PHONE = 15;

// Icon + "what to do" per problem type.
const PROBLEM_UI = {
  storageFree: { Icon: HardDrive, todo: (s) => `Automatic free up space runs at ${s.belowPct}% of the device's storage or less. If a device keeps filling up, open it and check its apps.` },
  storageHealth: { Icon: HardDrive, todo: () => 'Reported by the monitoring app; it follows free storage.' },
  crashes: { Icon: Bug, todo: () => 'Open the device and collect its logs; a newer player version often fixes repeated crashes.' },
  reboots: { Icon: RotateCcw, todo: () => 'Usually a weak power adapter or overheating - check the power supply at the venue.' },
  cpuTemp: { Icon: Thermometer, todo: () => 'Check ventilation - the box is often closed in a cabinet or right behind the TV.' },
  ramFree: { Icon: MemoryStick, todo: () => 'Restart the player, or reboot the device outside opening hours.' },
  packetLoss: { Icon: Wifi, todo: () => 'Venue network - weak Wi-Fi signal or a bad cable. A cable connection fixes most cases.' },
  latency: { Icon: Wifi, todo: () => 'Venue network is slow - check the router or the internet line.' },
  healthScore: { Icon: HeartPulse, todo: () => 'The score sums up the device\'s other problems - fix those first.' },
  flapping: { Icon: Activity, todo: () => 'Unstable power or network at the venue - the device keeps dropping and coming back.' },
  offlineHours: { Icon: WifiOff, todo: () => 'Device is switched off or has no internet - call the venue.' },
  playerStopped: { Icon: CirclePause, todo: () => 'The device is online but the player is not playing - start it from the device Controls tab.' },
  silent: { Icon: Cpu, todo: () => 'Marked online but the monitoring app stopped reporting - reboot the device.' },
  versionDrift: { Icon: PackageOpen, todo: () => 'Older player or monitoring than the released one - update it from Apps or the AOSP updater.' },
  anomaly: { Icon: TrendingUp, todo: () => 'Drops much more often than its own normal - something changed at the venue.' },
  otaStuck: { Icon: CloudDownload, todo: () => 'The device has not installed the new firmware. Its logs were requested automatically - open the device and check them; OTA can be switched off/on on the device Controls tab.' },
};
const uiOf = (code) => PROBLEM_UI[code] || { Icon: AlertTriangle, todo: () => '' };

const pct = (v) => (v === null || v === undefined ? '–' : `${v}%`);
const groupLabel = (key) => (key ? decodeURIComponent(key) : 'No group');
const asDate = (v) => (v && typeof v.toDate === 'function' ? v.toDate() : v ? new Date(v) : null);
const shortDate = (d) => d.toLocaleString([], { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

function ago(date, now) {
  if (!date) return '–';
  const min = Math.max(0, Math.round((now - date.getTime()) / 60000));
  if (min < 1) return 'just now';
  if (min < 60) return `${min} min ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h} h ${min % 60} min ago`;
  return `${Math.floor(h / 24)} d ago`;
}

// true below the Tailwind `sm` breakpoint (fewer chart ticks)
function useNarrow() {
  const query = '(max-width: 639px)';
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia(query).matches);
  useEffect(() => {
    const m = window.matchMedia(query);
    const on = () => setNarrow(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return narrow;
}

function SeverityPill({ critical }) {
  return critical ? (
    <span className="inline-flex items-center gap-1 rounded-full font-medium bg-red-500/15 text-red-300 border border-red-500/30 shrink-0 text-[11px] px-2 py-0.5">
      <ShieldAlert size={12} /> Critical
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full font-medium bg-amber-500/15 text-amber-300 border border-amber-500/30 shrink-0 text-[11px] px-2 py-0.5">
      <AlertTriangle size={12} /> Warning
    </span>
  );
}

// One selectable problem-type card.
function TypeCard({ selected, onClick, Icon, label, total, critical }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`text-left rounded-xl p-3 sm:p-4 border transition-colors ${selected
        ? 'bg-brand/10 border-brand/60'
        : 'bg-surface-inset border-white/[0.06] hover:border-white/[0.18] hover:bg-surface-inset-hi'}`}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={`w-8 h-8 sm:w-9 sm:h-9 rounded-lg flex items-center justify-center shrink-0 ${critical ? 'bg-red-500/15 text-red-300' : total ? 'bg-amber-500/15 text-amber-300' : 'bg-white/[0.04] text-gray-500'}`}>
          <Icon size={18} />
        </span>
        <span className="text-xl sm:text-2xl font-bold text-white">{total}</span>
      </div>
      <p className="mt-2 sm:mt-3 text-[13px] sm:text-sm font-medium text-gray-200 leading-tight">{label}</p>
      <p className="mt-1 text-xs">
        {critical > 0 && <span className="text-red-300">{critical} critical</span>}
        {critical > 0 && total - critical > 0 && <span className="text-gray-600"> · </span>}
        {total - critical > 0 && <span className="text-amber-300">{total - critical} warning</span>}
        {total === 0 && <span className="text-gray-500">none</span>}
      </p>
    </button>
  );
}

export default function FleetHealth({ summary: summaryProp }) {
  const navigate = useNavigate();
  const problemsRef = useRef(null);
  const [params, setParams] = useSearchParams();
  const [live, setLive] = useState(undefined);
  const [now, setNow] = useState(Date.now());
  const [allGroups, setAllGroups] = useState(false);
  const [allRows, setAllRows] = useState(false);
  const narrow = useNarrow();

  const summary = summaryProp || live;

  // Filters live in the URL, the group by its readable name (?group=CARGO+CAR); the summary uses the RTDB key.
  const type = params.get('problem') || '';
  const groupParam = params.get('group');
  const keyOf = (name) => ((summary?.groups || []).find((g) => g.key && groupLabel(g.key) === name) || {}).key || name;
  const group = groupParam === null ? null : groupParam === NO_GROUP ? '' : keyOf(groupParam); // null = all groups, '' = no group
  const search = params.get('q') || '';
  const setParam = (changes) => setParams((cur) => {
    const next = new URLSearchParams(cur);
    Object.entries(changes).forEach(([key, v]) => (v === null || v === '' ? next.delete(key) : next.set(key, v)));
    return next;
  }, { replace: true });
  const setType = (v) => setParam({ problem: v });
  const setGroup = (v) => setParam({ group: v === null ? null : v === '' ? NO_GROUP : groupLabel(v), problem: null });
  const setSearch = (v) => setParam({ q: v });

  useEffect(() => {
    if (summaryProp) return undefined;
    return onSnapshot(
      doc(analyticsDb, 'fleet', 'summary'),
      (snap) => setLive(snap.exists() ? snap.data() : null),
      () => setLive(null),
    );
  }, [summaryProp]);

  // keeps "x min ago" honest between scans
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60000);
    return () => clearInterval(t);
  }, []);

  const k = summary?.kpis || {};
  const all = useMemo(() => summary?.top || [], [summary]);
  const auto = summary?.autoFreeSpace || { belowPct: 15 };

  // Devices of the selected group (the problem list holds every problem device).
  const inGroup = useMemo(() => (group === null ? all : all.filter((r) => (r.group || '') === group)), [all, group]);

  // Problem-type cards for the current group scope.
  const types = useMemo(() => {
    const m = {};
    inGroup.forEach((r) => r.problems.forEach((p) => {
      m[p.code] = m[p.code] || { code: p.code, total: 0, critical: 0 };
      m[p.code].total += 1;
      if (p.severity === 2) m[p.code].critical += 1;
    }));
    return Object.values(m).sort((a, b) => b.critical - a.critical || b.total - a.total);
  }, [inGroup]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = type ? inGroup.filter((r) => r.problems.some((p) => p.code === type)) : inGroup;
    if (q) list = list.filter((r) => `${r.name} ${r.mac} ${groupLabel(r.group)}`.toLowerCase().includes(q));
    if (type) {
      const sev = (r) => (r.problems.find((p) => p.code === type) || {}).severity || 0;
      list = [...list].sort((a, b) => sev(b) - sev(a) || b.score - a.score);
    }
    return list;
  }, [inGroup, type, search]);

  // Top problem types per group (shown in the group list, so the problem is visible without clicking).
  const groupTypes = useMemo(() => {
    const m = {};
    all.forEach((r) => {
      const g = r.group || '';
      m[g] = m[g] || {};
      r.problems.forEach((p) => { m[g][p.code] = (m[g][p.code] || 0) + 1; });
    });
    return Object.fromEntries(Object.entries(m).map(([g, c]) => [g, Object.entries(c).sort((a, b) => b[1] - a[1]).slice(0, 3)]));
  }, [all]);

  // If the selected type disappears from the scope (other group / new scan), fall back to all problems.
  const typeGone = !!summary && !!type && !types.some((t) => t.code === type);
  useEffect(() => {
    if (typeGone) setParam({ problem: null });
  }, [typeGone]); // eslint-disable-line

  const header = (generatedAt) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5 sm:mb-6">
      <div className="flex items-center gap-3 min-w-0">
        <div className="brand-icon-badge"><HeartPulse size={18} /></div>
        <div className="min-w-0">
          <h1 className="text-xl font-semibold text-white">Fleet health</h1>
          {summary && (
            <p className="text-xs text-gray-500">
              Last scan {ago(generatedAt, now)} · every {summary.scanEveryMinutes || 15} min
              <span className="hidden sm:inline">
                {' · '}{k.inactive ?? 0} inactive{k.retired ? ` and ${k.retired} in DELETED` : ''} not counted
              </span>
            </p>
          )}
        </div>
      </div>
      <button type="button" onClick={() => navigate('/analytics-rules')} className="btn-secondary self-start sm:self-auto sm:ml-auto">
        <SlidersHorizontal size={ICON} /> Rules
      </button>
    </div>
  );

  if (summary === undefined) return header(null);
  if (summary === null) {
    return (
      <>
        {header(null)}
        <PanelCard icon={<CircleGauge size={ICON} />} title="Fleet health">
          <p className="text-sm text-gray-500">The first fleet scan has not finished yet. It runs every 15 minutes.</p>
        </PanelCard>
      </>
    );
  }

  const generatedAt = asDate(summary.generatedAt);
  const groups = (summary.groups || []).filter((g) => g.total > 0);
  const problemGroups = groups.filter((g) => g.problemDevices > 0);
  const known = summary.mutedList || [];
  const series = summary.series || [];
  const inScopeCritical = inGroup.filter((r) => r.problems.some((p) => p.severity === 2)).length;
  const selectedMeta = type ? RULE_META[type] : null;
  const groupSelectValue = group === null ? '' : group === '' ? NO_GROUP : group;
  const showGroup = (key) => {
    setGroup(key);
    problemsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const chartData = {
    labels: series.map((d) => `${d.day.slice(6, 8)}.${d.day.slice(4, 6)}.`),
    datasets: [{
      label: 'Availability',
      data: series.map((d) => d.availability),
      borderColor: '#ff7a1f',
      backgroundColor: 'rgba(235, 94, 0, 0.12)',
      borderWidth: 2,
      pointRadius: series.length > 1 ? 0 : 4,
      pointHoverRadius: 5,
      pointBackgroundColor: '#ff7a1f',
      fill: true,
      tension: 0.3,
      spanGaps: true,
    }],
  };
  const chartOptions = {
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: '#1a1a1a', borderColor: 'rgba(255,255,255,0.1)', borderWidth: 1,
        titleColor: '#D1D5DB', bodyColor: '#D1D5DB', displayColors: false,
        callbacks: { label: (c) => `Availability ${c.parsed.y}%` },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#6B7280', maxTicksLimit: narrow ? 5 : 10, maxRotation: 0 },
        border: { color: 'rgba(255,255,255,0.08)' },
      },
      y: {
        suggestedMin: 50, max: 100,
        grid: { color: 'rgba(255,255,255,0.05)' }, border: { display: false },
        ticks: { color: '#6B7280', callback: (v) => `${v}%`, maxTicksLimit: narrow ? 4 : 6 },
      },
    },
  };

  return (
    <>
      {header(generatedAt)}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
        <StatCard
          icon={<CircleGauge size={ICON} />}
          title="Availability"
          value={pct(k.availabilityNow)}
          subtitle={`7 days ${pct(k.availability7d)} · 30 days ${pct(k.availability30d)}`}
        />
        <StatCard
          icon={<AlertTriangle size={ICON} />}
          title="With Problems"
          value={k.problemDevices ?? 0}
          subtitle={`${k.critical ?? 0} critical · ${k.warning ?? 0} warning`}
          subtitleTone={k.critical ? 'danger' : undefined}
          onClick={() => showGroup(null)}
        />
        <StatCard
          icon={<WifiOff size={ICON} />}
          title="Offline Now"
          value={k.offline ?? 0}
          subtitle={`of ${k.active ?? 0} active devices`}
          onClick={() => navigate('/devices?status=offline')}
        />
        <StatCard
          icon={<BellOff size={ICON} />}
          title="Known Issues"
          value={k.muted ?? 0}
          subtitle="Muted - kept out of the problem list"
          onClick={known.length ? () => document.getElementById('fleet-known')?.scrollIntoView({ behavior: 'smooth' }) : undefined}
        />
      </div>

      {/* Problems: filter bar -> type cards -> device list */}
      <section ref={problemsRef} className="card-chrome mb-6 scroll-mt-16">
        <header className="flex items-center gap-3 mb-3">
          <div className="brand-icon-badge"><ShieldAlert size={ICON} /></div>
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-white">Problems</h2>
            <p className="text-xs text-gray-500">
              {inGroup.length} device{inGroup.length === 1 ? '' : 's'} with problems
              {inScopeCritical ? `, ${inScopeCritical} critical` : ''}
              {group !== null ? ` in ${groupLabel(group)}` : ''}
              <span className="hidden sm:inline"> · pick a card to see one problem</span>
            </p>
          </div>
        </header>

        {/* Stays under the top bar while the list scrolls on a phone. */}
        <div className="sticky top-14 z-20 -mx-4 px-4 py-2 mb-3 bg-surface-card/95 backdrop-blur md:static md:mx-0 md:px-0 md:py-0 md:mb-4 md:bg-transparent md:backdrop-blur-none">
          <div className="flex flex-col sm:flex-row gap-2">
            <label className="relative flex-1 sm:max-w-xs">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Find device…"
                aria-label="Find device"
                className="input-field pl-8 py-2 w-full"
              />
            </label>
            <label className="relative sm:w-64">
              <Layers size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              <select
                value={groupSelectValue}
                onChange={(e) => setGroup(e.target.value === '' ? null : e.target.value === NO_GROUP ? '' : e.target.value)}
                aria-label="Group"
                className="input-field pl-8 pr-8 py-2 w-full appearance-none"
              >
                <option value="">All groups</option>
                {problemGroups.map((g) => (
                  <option key={g.key || NO_GROUP} value={g.key || NO_GROUP}>
                    {groupLabel(g.key)} ({g.problemDevices})
                  </option>
                ))}
                {group !== null && !problemGroups.some((g) => (g.key || '') === group) && (
                  <option value={groupSelectValue}>{groupLabel(group)} (0)</option>
                )}
              </select>
              <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            </label>
          </div>
        </div>

        {inGroup.length === 0 ? (
          <p className="text-sm text-gray-400 py-6 text-center">
            No problems{group !== null ? ` in ${groupLabel(group)}` : ''} right now.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-2 sm:gap-3 mb-4">
              <TypeCard
                selected={!type}
                onClick={() => setType('')}
                Icon={ShieldAlert}
                label="All problems"
                total={inGroup.length}
                critical={inScopeCritical}
              />
              {types.map((t) => (
                <TypeCard
                  key={t.code}
                  selected={type === t.code}
                  onClick={() => setType(type === t.code ? '' : t.code)}
                  Icon={uiOf(t.code).Icon}
                  label={PROBLEM_LABEL[t.code] || t.code}
                  total={t.total}
                  critical={t.critical}
                />
              ))}
            </div>

            {selectedMeta && (
              <div className="card-inset p-3 sm:p-4 mb-4 flex items-start gap-3">
                <Lightbulb size={18} className="text-brand-400 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{selectedMeta.label}</p>
                  <p className="text-sm text-gray-400">{selectedMeta.help}</p>
                  <p className="text-sm text-gray-300 mt-1">{uiOf(type).todo(auto)}</p>
                </div>
              </div>
            )}

            <div className="flex items-center justify-between text-xs text-gray-500 mb-2 px-1">
              <span>{rows.length} device{rows.length === 1 ? '' : 's'}{type ? ` · ${PROBLEM_LABEL[type]}` : ''}</span>
              <span className="hidden md:inline">Click a device for its history</span>
            </div>

            <div className="space-y-2 md:max-h-[40rem] md:overflow-y-auto md:pr-1">
              {rows.length === 0 && <p className="text-sm text-gray-500 py-4 text-center">No device matches.</p>}
              {rows.map((r, i) => {
                const shown = type ? r.problems.filter((p) => p.code === type) : r.problems;
                const others = type ? r.problems.length - shown.length : 0;
                const critical = shown.some((p) => p.severity === 2);
                const since = asDate(r.since);
                return (
                  <button
                    type="button"
                    key={r.mac}
                    onClick={() => navigate(`/devices/${macToSlug(r.mac)}#logs`)}
                    className={`w-full text-left card-inset card-inset-hover p-3 sm:p-4 items-start md:items-center gap-3 md:gap-4 ${!allRows && i >= DEVICES_ON_PHONE ? 'hidden md:flex' : 'flex'}`}
                  >
                    <span
                      className={`w-2.5 h-2.5 rounded-full shrink-0 mt-1.5 md:mt-0 ${r.status === 'online' ? 'bg-green-400' : 'bg-gray-500'}`}
                      title={r.status === 'online' ? 'Online' : 'Offline'}
                    />
                    <div className="min-w-0 flex-1 flex flex-col md:flex-row md:items-center gap-1.5 md:gap-4">
                      <div className="min-w-0 md:w-56 md:shrink-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <p className="text-[15px] font-semibold text-white truncate">{r.name || r.mac}</p>
                          <span className="md:hidden ml-auto"><SeverityPill critical={critical} /></span>
                        </div>
                        <p className="text-xs text-gray-500 truncate">
                          {groupLabel(r.group)} · {r.status === 'online' ? 'Online' : 'Offline'}
                        </p>
                      </div>
                      <div className="min-w-0 flex-1">
                        {type ? (
                          <div className="flex items-baseline gap-x-3 gap-y-0.5 flex-wrap">
                            <span className={`text-base sm:text-lg font-semibold ${critical ? 'text-red-300' : 'text-amber-300'}`}>
                              {problemValue(shown[0]) || PROBLEM_LABEL[type]}
                            </span>
                            {others > 0 && <span className="text-xs text-gray-500">+{others} other problem{others === 1 ? '' : 's'}</span>}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1.5">
                            {shown.map((p) => (
                              <span
                                key={`${p.code}-${p.value}`}
                                className={`px-2 py-1 rounded-md text-xs border ${p.severity === 2 ? 'border-red-500/30 text-red-200 bg-red-500/[0.08]' : 'border-amber-500/30 text-amber-200 bg-amber-500/[0.08]'}`}
                              >
                                {PROBLEM_LABEL[p.code] || p.code}
                                {problemValue(p) ? <span className="text-gray-400"> · {problemValue(p)}</span> : null}
                              </span>
                            ))}
                          </div>
                        )}
                        {since && (
                          <p className="text-[11px] text-gray-500 mt-1">
                            Since {shortDate(since)}<span className="hidden sm:inline"> · {r.mac}</span>
                          </p>
                        )}
                      </div>
                    </div>
                    <span className="hidden md:inline-flex"><SeverityPill critical={critical} /></span>
                    <ChevronRight size={18} className="text-gray-500 shrink-0 mt-0.5 md:mt-0" />
                  </button>
                );
              })}
            </div>
            {rows.length > DEVICES_ON_PHONE && (
              <button
                type="button"
                onClick={() => setAllRows(!allRows)}
                className="md:hidden btn-secondary w-full justify-center mt-3"
              >
                <ChevronDown size={ICON} className={allRows ? 'rotate-180' : ''} />
                {allRows ? 'Show fewer devices' : `Show all ${rows.length} devices`}
              </button>
            )}
          </>
        )}
      </section>

      {/* Known issues (muted) */}
      {known.length > 0 && (
        <div id="fleet-known" className="mb-6 scroll-mt-16">
          <PanelCard icon={<BellOff size={ICON} />} title={`Known issues (muted) · ${known.length}`}>
            <div className="space-y-2 md:max-h-80 md:overflow-y-auto md:pr-1">
              {known.map((r) => (
                <button
                  type="button"
                  key={r.mac}
                  onClick={() => navigate(`/devices/${macToSlug(r.mac)}#logs`)}
                  className="w-full text-left card-inset card-inset-hover p-3 flex items-center gap-3"
                >
                  <span className={`w-2 h-2 rounded-full shrink-0 ${r.status === 'online' ? 'bg-green-400' : 'bg-gray-500'}`} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white truncate">
                      {r.name || r.mac}
                      <span className="text-gray-400"> · {r.reason || 'no reason given'}</span>
                    </p>
                    <p className="text-[11px] text-gray-500 truncate">
                      {groupLabel(r.group)}{r.kind === 'group' ? ' (whole group muted)' : ''}
                      {' · '}{r.until ? `until ${new Date(r.until).toLocaleDateString()}` : 'no end date'}
                      {' · '}{r.problems.map((p) => PROBLEM_LABEL[p.code] || p.code).join(', ')}
                    </p>
                  </div>
                  <ChevronRight size={ICON} className="text-gray-500 shrink-0" />
                </button>
              ))}
            </div>
          </PanelCard>
        </div>
      )}

      {/* Groups + availability */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
        <div className="lg:col-span-2">
          <PanelCard icon={<Layers size={ICON} />} title="Groups by problems">
            <p className="text-xs text-gray-500 -mt-2 mb-3">Tap a group to see its problems above.</p>
            {groups.length === 0 ? (
              <p className="text-sm text-gray-500">No active groups.</p>
            ) : (
              <>
                <div className="space-y-2 lg:max-h-[30rem] lg:overflow-y-auto lg:pr-1">
                  {groups.map((g, i) => {
                    const online = g.total ? Math.round((g.online / g.total) * 100) : 0;
                    const selected = group === g.key;
                    const tops = groupTypes[g.key] || [];
                    return (
                      <div
                        key={g.key || NO_GROUP}
                        role="button"
                        tabIndex={0}
                        onClick={() => showGroup(g.key)}
                        onKeyDown={(e) => { if (e.key === 'Enter') showGroup(g.key); }}
                        className={`rounded-xl p-3 border cursor-pointer transition-colors ${!allGroups && i >= GROUPS_ON_PHONE ? 'hidden lg:block' : ''} ${selected
                          ? 'bg-brand/10 border-brand/60'
                          : 'bg-surface-inset border-white/[0.06] hover:border-white/[0.18] hover:bg-surface-inset-hi'}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-sm font-semibold text-white truncate">{groupLabel(g.key)}</p>
                          <div className="flex items-center gap-1 shrink-0">
                            {g.critical > 0 ? (
                              <span className="text-xs text-red-300">{g.critical} critical</span>
                            ) : g.problemDevices > 0 ? (
                              <span className="text-xs text-amber-300">{g.problemDevices} warning</span>
                            ) : (
                              <span className="text-xs text-green-400">OK</span>
                            )}
                            {g.key && (
                              <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); navigate(`/groups/${g.key}`); }}
                                className="text-gray-500 hover:text-gray-200 p-1.5 -m-0.5 rounded-md"
                                title="Open the group page"
                                aria-label={`Open group ${groupLabel(g.key)}`}
                              >
                                <ExternalLink size={14} />
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {g.problemDevices} of {g.total} devices with problems · {online}% online
                        </p>
                        {tops.length > 0 && (
                          <p className="text-xs text-gray-500 mt-0.5 truncate">
                            {tops.map(([code, n]) => `${PROBLEM_LABEL[code] || code} ${n}`).join(' · ')}
                          </p>
                        )}
                        <div className="mt-2 h-1.5 rounded-full bg-white/[0.06] overflow-hidden" title={`${online}% online`}>
                          <div className="h-full rounded-full bg-green-500/60" style={{ width: `${online}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                {groups.length > GROUPS_ON_PHONE && (
                  <button
                    type="button"
                    onClick={() => setAllGroups(!allGroups)}
                    className="lg:hidden btn-secondary w-full justify-center mt-3"
                  >
                    <ChevronDown size={ICON} className={allGroups ? 'rotate-180' : ''} />
                    {allGroups ? 'Show fewer groups' : `Show all ${groups.length} groups`}
                  </button>
                )}
              </>
            )}
          </PanelCard>
        </div>

        <div className="lg:col-span-3">
          <PanelCard icon={<LineChart size={ICON} />} title="Availability, last 30 days">
            <div className="h-56 sm:h-72">
              <Line data={chartData} options={chartOptions} aria-label="Fleet availability per day, last 30 days" />
            </div>
            <p className="text-xs text-gray-500 mt-3">
              {series.length < 2
                ? 'The chart fills in day by day as scans run.'
                : 'Share of active devices online, averaged over every scan of the day.'}
            </p>
          </PanelCard>
        </div>
      </div>
    </>
  );
}
